import ChunkError from './ChunkError.vue';
import ChunkLoading from './ChunkLoading.vue';

/**
 * Задержка перед показом спиннера. При тёплом кеше чанк приезжает за единицы
 * миллисекунд, и без паузы спиннер мигал бы на один кадр — заметнее, чем его
 * отсутствие.
 */
const DELAY = 200;

/**
 * Потолок ожидания чанка.
 *
 * Своё значение нужно потому, что дефолт webpack (`chunkLoadTimeout`) — 120
 * секунд: зависший, но не отклонённый запрос держал бы спиннер две минуты,
 * ровно на той мобильной сети, ради которой бандл и делится.
 *
 * 90 секунд выбраны с запасом к честной загрузке. Самый тяжёлый чанк СЕГОДНЯ
 * (после Task 8 плана bundle-split-froala, вынесшего движок Froala из
 * app.js/app-service.js в асинхронный чанк) — не editorjs, как было на
 * момент прежней оценки, а froala-editor: 4 491 109 Б, то есть 1 192 253 Б
 * в gzip (замерено `gzip -c public/js/froala-editor.*.js | wc -c`, а не
 * взято на веру из старого комментария). На 400 кбит/с (плохой LTE) это
 * ~23,8 с, на 200 кбит/с — ~47,7 с. До потолка остаётся почти двукратный
 * запас на RTT, TLS-хэндшейк и разбор/исполнение модуля, а до
 * webpack-овских 120 с — 30 с (четверть), то есть свой таймаут по-прежнему
 * успевает сработать раньше дефолтного и не превращается в фикцию.
 *
 * Если появится чанк тяжелее — потолок нужно пересчитать по той же
 * арифметике, а не оставлять цифру, посчитанную под прежний самый тяжёлый
 * чанк (именно так этот комментарий и устарел в прошлый раз).
 *
 * Занижать нельзя: Vue по таймауту показывает ошибку НАВСЕГДА (состояние
 * живёт на объекте-фабрике), даже если загрузка в итоге дошла бы. Поэтому
 * потолок — про «запрос не вернётся никогда», а не про «долго».
 */
const TIMEOUT = 90000;

/**
 * Описание асинхронного компонента для ленивого чанка: общие состояния
 * загрузки и ошибки плюс общие тайминги.
 *
 * ВАЖНО, КАК ВЫЗЫВАТЬ: результат обязан создаваться ОДИН раз, на уровне
 * модуля, и уже готовая функция — попадать в `components`. Vue кеширует резолв
 * на самом объекте функции-фабрики (`resolveAsyncComponent`: ранний возврат по
 * `factory.resolved`, вход в загрузку только пока `factory.owners` не
 * определён). Вызов `lazyChunk(...)` внутри `components(){}` или в вычисляемом
 * свойстве отдавал бы новую функцию на каждый рендер, и кеш не работал бы:
 * чанк перезапрашивался бы, а состояния мигали заново.
 *
 * @param {() => Promise<any>} loader обёртка над import() чанка; зовётся один
 *   раз, в момент первого рендера компонента, а не при загрузке модуля
 */
export default function lazyChunk(loader) {
    return () => ({
        component: loader(),
        loading: ChunkLoading,
        error: ChunkError,
        delay: DELAY,
        timeout: TIMEOUT,
    });
}
